import React, { useEffect, useState, useCallback } from 'react';
import { pursuitApi } from '../../api/client';

// Seller pursuit: Capture Plan. AI-drafts a win strategy grounded in the
// opportunity, its documents, and RFQ->Award history. Advisory; the user edits
// nothing inline in v1 beyond regenerating/saving (fields are AI-authored lists).

interface Props { oppId: string; }

const money = (n?: number) => (n == null ? '—' : '$' + Math.round(n).toLocaleString());

export default function CapturePlanTab({ oppId }: Props) {
  const [plan, setPlan] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pursuitApi.getCapturePlan(oppId);
      setPlan(res.capture_plan && Object.keys(res.capture_plan).length ? res.capture_plan : null);
    } catch (e: any) { setError(e?.message || 'Could not load capture plan.'); }
    setLoading(false);
  }, [oppId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true); setError(''); setMsg('');
    try {
      const res = await pursuitApi.generateCapturePlan(oppId, true); // save on generate
      setPlan(res.capture_plan);
      setMsg('Capture plan drafted and saved. Review and refine.');
    } catch (e: any) { setError(e?.message || 'AI draft failed.'); }
    setGenerating(false);
  };

  const g = plan?.grounding;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontSize: 18, margin: 0 }}>Capture Plan</h2>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0', maxWidth: 640 }}>
            Your win strategy for this pursuit — themes, strengths, gaps, and discriminators, drafted
            from the solicitation and historical award data. Advisory; you make the call.
          </p>
        </div>
        <button onClick={generate} disabled={generating}
          style={{ padding: '9px 16px', background: generating ? '#93c5fd' : '#4338ca', color: 'white',
                   border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: generating ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          {generating ? 'Drafting…' : plan ? 'Re-draft with AI' : 'Draft with AI'}
        </button>
      </div>

      {error && <div role="alert" style={{ color: '#991b1b', background: '#fee2e2', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{error}</div>}
      {msg && <div style={{ color: '#166534', background: '#dcfce7', padding: '10px 14px', borderRadius: 8, marginBottom: 12 }}>{msg}</div>}

      {loading ? (
        <div style={{ color: '#6b7280', fontSize: 13 }}>Loading…</div>
      ) : !plan ? (
        <div style={{ padding: 24, border: '1px dashed #cbd5e1', borderRadius: 12, textAlign: 'center', color: '#64748b' }}>
          No capture plan yet. Click <strong>Draft with AI</strong> to generate one grounded in this
          opportunity and comparable past awards.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <ListCard title="Win themes" items={plan.win_themes} />
          <ListCard title="Our strengths" items={plan.our_strengths} />
          <ListCard title="Gaps to close" items={plan.gaps} accent="#b45309" />
          <ListCard title="Discriminators" items={plan.discriminators} />
          <ListCard title="Recommended actions" items={plan.recommended_actions} accent="#4338ca" />
          {plan.competitor_notes && (
            <TextCard title="Competitor / incumbent notes" body={plan.competitor_notes} />
          )}
          {plan.price_to_win_signal && (
            <TextCard title="Price-to-win signal" body={plan.price_to_win_signal} />
          )}
          {g && (
            <div style={{ fontSize: 11, color: '#94a3b8' }}>
              Grounded in {g.comparable_count} comparable awards (scope: {g.scope})
              {g.award_value && <> — median {money(g.award_value.median)}, range {money(g.award_value.min)}–{money(g.award_value.max)}</>}.
              Advisory; verify before use.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ListCard({ title, items, accent }: { title: string; items?: string[]; accent?: string }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: 'white' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: accent || '#334155', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>{title}</div>
      <ul style={{ margin: 0, paddingLeft: 18, color: '#1e293b', fontSize: 13, lineHeight: 1.6 }}>
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}

function TextCard({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, background: 'white' }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{title}</div>
      <p style={{ margin: 0, color: '#1e293b', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{body}</p>
    </div>
  );
}
